import { qz } from "../client";
import { createNamespaceStore } from "../internal/createNamespaceStore";
import {
  type PrinterCallbackEvent,
  type PrinterEvent,
  type PrinterJobQueue,
  type PrinterJobStatus,
  type PrinterListeningOptions,
  type PrinterState,
  type PrinterStatus,
} from "./types";

const MAX_PRINTER_EVENTS = 100;
const TERMINAL_JOB_STATUS_TEXTS = new Set([
  "ABORTED",
  "CANCELED",
  "COMPLETE",
  "DELETED",
]);

const toText = (value: unknown): null | string => {
  if (typeof value === "string") return value;

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value instanceof String
  )
    return String(value);

  if (value === null || value === undefined || typeof value === "function")
    return null;

  try {
    const normalized = String(value);
    return normalized === "[object Object]" ? null : normalized;
  } catch {
    return null;
  }
};

const toNumber = (value: unknown): null | number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value instanceof Number) {
    const parsed = Number(value.valueOf());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toEventType = (value: unknown): PrinterEvent["eventType"] => {
  const eventType = toText(value);
  return eventType === "PRINTER" ||
    eventType === "JOB" ||
    eventType === "JOB_DATA"
    ? eventType
    : null;
};

const toStatusCode = (value: unknown): PrinterStatus["lastStatusCode"] => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const text = toText(value);
  return text === null ? null : text;
};

const toPrinterStatus = (
  event: PrinterEvent,
  updatedAt: number,
): PrinterStatus | null => {
  if (event.eventType === "JOB" || event.eventType === "JOB_DATA") return null;

  return {
    lastActionType: event.kind === "ACTION" ? event.actionType : null,
    lastEventKind: event.kind,
    lastEventType: event.eventType,
    lastException: event.kind === "ERROR" ? event.exception : null,
    lastJobId: event.jobId,
    lastJobName: event.jobName,
    lastMessage: event.message,
    lastOutput: event.kind === "RECEIVE" ? event.output : null,
    lastSeverity: event.severity,
    lastStatusCode: event.statusCode,
    printerName: event.printerName,
    statusText: event.statusText,
    updatedAt,
  };
};

const toPrinterJobStatus = (
  event: PrinterEvent,
  updatedAt: number,
): PrinterJobStatus | null => {
  if (event.eventType !== "JOB" || event.jobId === null) return null;

  return {
    jobId: event.jobId,
    jobName: event.jobName,
    lastEventType: "JOB",
    lastMessage: event.message,
    lastSeverity: event.severity,
    lastStatusCode: event.statusCode,
    printerName: event.printerName,
    statusText: event.statusText,
    updatedAt,
  };
};

const isTerminalJobStatus = (statusText: string): boolean =>
  TERMINAL_JOB_STATUS_TEXTS.has(statusText.trim().toUpperCase());

const updatePrinterJobQueue = (
  queue: PrinterJobQueue | undefined,
  status: PrinterJobStatus,
): PrinterJobQueue => {
  const currentQueue = queue ?? { activeJobIds: [], jobsById: {} };
  const isTerminal = isTerminalJobStatus(status.statusText);
  const isActive = currentQueue.activeJobIds.includes(status.jobId);
  let activeJobIds = currentQueue.activeJobIds;

  if (isTerminal && isActive) {
    activeJobIds = currentQueue.activeJobIds.filter(
      (jobId) => jobId !== status.jobId,
    );
  } else if (!isTerminal && !isActive) {
    activeJobIds = [...currentQueue.activeJobIds, status.jobId];
  }

  return {
    activeJobIds,
    jobsById: {
      ...currentQueue.jobsById,
      [status.jobId]: status,
    },
  };
};

const parsePrinterEvent = (raw: unknown): PrinterEvent | null => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const printerName = toText(data.printerName);
  const eventType = toEventType(data.eventType);
  const kind = toText(data.type);
  const receivedAt = Date.now();

  if (!printerName) return null;

  if (kind === "ACTION" && eventType === "JOB_DATA") {
    const jobId = toNumber(data.jobID) ?? toNumber(data.jobId);

    if (jobId === null) return null;

    return {
      data: toText(data.data),
      eventType: "JOB_DATA",
      jobId,
      jobName: toText(data.jobName),
      kind: "JOB_DATA",
      printerName,
      raw: raw as PrinterCallbackEvent,
      receivedAt,
    };
  }

  const statusText = toText(data.statusText) ?? toText(data.status);

  if (!statusText) return null;

  const base = {
    eventType,
    jobId: toNumber(data.jobId),
    jobName: toText(data.jobName),
    message: toText(data.message),
    printerName,
    raw: raw as PrinterCallbackEvent,
    receivedAt,
    severity: toText(data.severity),
    statusCode: toStatusCode(data.statusCode),
    statusText,
  };

  if (kind === "ERROR" || toText(data.exception)) {
    return {
      ...base,
      exception: toText(data.exception) ?? base.message ?? base.statusText,
      jobId: null,
      jobName: null,
      kind: "ERROR",
      message: null,
      severity: null,
      statusCode: null,
    };
  }

  if (kind === "RECEIVE" || "output" in data) {
    return {
      ...base,
      jobId: null,
      jobName: null,
      kind: "RECEIVE",
      message: null,
      output: data.output,
      severity: null,
      statusCode: null,
    };
  }

  if (kind === "ACTION" || toText(data.actionType)) {
    return {
      ...base,
      actionType: toText(data.actionType) ?? kind,
      kind: "ACTION",
    };
  }

  return { ...base, kind: "STATUS" };
};

export const printerStore = createNamespaceStore<PrinterState, PrinterEvent>({
  applyEvent: (state, event, at) => {
    const nextPrinterStatus = toPrinterStatus(event, at);
    const nextJobStatus = toPrinterJobStatus(event, at);
    const nextJobQueuesByPrinter = nextJobStatus
      ? {
          ...state.jobQueuesByPrinter,
          [nextJobStatus.printerName]: updatePrinterJobQueue(
            state.jobQueuesByPrinter[nextJobStatus.printerName],
            nextJobStatus,
          ),
        }
      : state.jobQueuesByPrinter;

    return {
      ...state,
      events: [...state.events, event].slice(-MAX_PRINTER_EVENTS),
      jobQueuesByPrinter: nextJobQueuesByPrinter,
      lastEventAt: at,
      statusByPrinter: nextPrinterStatus
        ? {
            ...state.statusByPrinter,
            [event.printerName]: nextPrinterStatus,
          }
        : state.statusByPrinter,
    };
  },
  createInitialState: () => ({
    events: [],
    isListening: false,
    jobQueuesByPrinter: {},
    lastEventAt: null,
    lastListenerError: null,
    lastStatusRefreshAt: null,
    listeningOptions: null,
    listeningPrinters: null,
    statusByPrinter: {},
  }),
  label: "printer",
  parseEvent: parsePrinterEvent,
  registerCallback: (handler) => qz.printers.setPrinterCallbacks(handler),
});

export const startPrinterListeningState = (
  printers: null | string[],
  options: PrinterListeningOptions | null,
) => {
  printerStore.setState((s) => ({
    ...s,
    isListening: true,
    listeningOptions: options,
    listeningPrinters: printers,
  }));
};

export const stopPrinterListeningState = () => {
  printerStore.setState((s) => ({
    ...s,
    isListening: false,
    listeningOptions: null,
    listeningPrinters: null,
  }));
};

export const markPrinterStatusRefresh = () => {
  printerStore.setState((s) => ({
    ...s,
    lastStatusRefreshAt: Date.now(),
  }));
};
